import { useContext } from 'react';

import { Card, Col, Row, Typography, Spin } from 'antd';

import { useQuery } from '@tanstack/react-query';

import LineChart from '../components/chart/LineChart';
import StatisticsTable from '../components/StatisticsTable';

import HeatmapChart from 'src/components/chart/HeatmapChart';
import {
  getAccountsGreeksPnl,
  getAccountsGreeks,
  getCumulativePnl,
  getDailyPerformance,
  getNav,
  getStatistics,
  getMaintenanceMargin,
} from 'src/services/dashboard.service';
import { AccountContext, DateContext } from 'src/components/layout/Main';
import { formatDate } from 'src/utils/util';
import navChart from 'src/components/chart/configs/navChart';
import pnlChart from 'src/components/chart/configs/pnlChart';

const { Title } = Typography;

function Home() {
  const { timeRange } = useContext(DateContext);
  const { filteredAccount } = useContext(AccountContext);

  const { data: statisticsData, isLoading: statisticsLoading } = useQuery({
    queryKey: [
      'statistics',
      {
        start_date: formatDate(timeRange[0]),
        end_date: formatDate(timeRange[1]),
      },
    ],
    queryFn: getStatistics,
  });
  const { data: navData, isLoading: navLoading } = useQuery({
    queryKey: [
      'nav',
      {
        start_date: formatDate(timeRange[0]),
        end_date: formatDate(timeRange[1]),
      },
    ],
    queryFn: getNav,
  });
  const { data: dailyPnlData, isLoading: dailyPnlLoading } = useQuery({
    queryKey: ['daily_pnl'],
    queryFn: getDailyPerformance,
  });
  const { data: cumulativePnlData, isLoading: cumulativePnlLoading } = useQuery(
    {
      queryKey: [
        'cumulative_pnl',
        {
          start_date: formatDate(timeRange[0]),
          end_date: formatDate(timeRange[1]),
        },
      ],
      queryFn: getCumulativePnl,
    }
  );
  const { data: greeksData, isLoading: greeksLoading } = useQuery({
    queryKey: [
      'accounts_greeks',
      {
        start_date: formatDate(timeRange[0]),
        end_date: formatDate(timeRange[1]),
      },
    ],
    queryFn: getAccountsGreeks,
  });
  const { data: greeksPnlData, isLoading: greeksPnlLoading } = useQuery({
    queryKey: [
      'accounts_greeks_pnl',
      {
        start_date: formatDate(timeRange[0]),
        end_date: formatDate(timeRange[1]),
      },
    ],
    queryFn: getAccountsGreeksPnl,
  });
  const { data: maintenanceMarginData, isLoading: maintenanceMarginLoading } =
    useQuery({
      queryKey: [
        'maintenance_margin',
        {
          start_date: formatDate(timeRange[0]),
          end_date: formatDate(timeRange[1]),
        },
      ],
      queryFn: getMaintenanceMargin,
    });

  return (
    <>
      <div className='layout-content'>
        <Row className='rowgap-vbox' gutter={[24, 0]}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Spin spinning={statisticsLoading} tip='Loading...'>
              <StatisticsTable
                data={
                  filteredAccount
                    ? statisticsData?.result.filter(
                        (item) =>
                          item.account_name === filteredAccount.account_name
                      )
                    : statisticsData?.result
                }
              />
            </Spin>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          <Col xs={24} sm={24} md={24} lg={12} xl={12} className='mb-24'>
            <Card bordered={false} className='criclebox h-full'>
              <Spin spinning={navLoading} tip='Loading...'>
                <LineChart
                  title='NAV'
                  height={350}
                  width='100%'
                  series={
                    filteredAccount
                      ? navData?.result.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : navData?.result
                  }
                  options={navChart.options}
                />
              </Spin>
            </Card>
          </Col>

          <Col xs={24} sm={24} md={24} lg={12} xl={12} className='mb-24'>
            <Card bordered={false} className='criclebox h-full'>
              <Spin spinning={dailyPnlLoading} tip='Loading...'>
                <HeatmapChart
                  title='Daily Performance'
                  height={350}
                  width='100%'
                  series={
                    filteredAccount
                      ? dailyPnlData?.result.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : dailyPnlData?.result
                  }
                />
              </Spin>
            </Card>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          <Col xs={24} sm={24} md={24} lg={12} xl={12} className='mb-24'>
            <Card bordered={false} className='criclebox h-full'>
              <Spin spinning={cumulativePnlLoading} tip='Loading...'>
                <LineChart
                  title='Cumulative PnL'
                  height={350}
                  width='100%'
                  series={
                    filteredAccount
                      ? cumulativePnlData?.result.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : cumulativePnlData?.result
                  }
                  options={pnlChart.options}
                />
              </Spin>
            </Card>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12} className='mb-24'>
            <Card bordered={false} className='criclebox h-full'>
              <Spin spinning={maintenanceMarginLoading} tip='Loading...'>
                <LineChart
                  title='Maintenance Margin'
                  height={350}
                  width='100%'
                  series={
                    filteredAccount
                      ? maintenanceMarginData?.result.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : maintenanceMarginData?.result
                  }
                  options={pnlChart.options}
                />
              </Spin>
            </Card>
          </Col>
        </Row>

        <div className='Header-top'>
          <Title level={5} className='mb-0'>
            Greeks
          </Title>
        </div>

        <Spin spinning={greeksLoading} tip='Loading...'>
          <Row gutter={[24, 0]}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Delta'
                  series={
                    filteredAccount
                      ? greeksData?.result?.net_delta?.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : greeksData?.result?.net_delta
                  }
                  options={pnlChart.options}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Gamma'
                  series={
                    filteredAccount
                      ? greeksData?.result?.gamma?.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : greeksData?.result?.gamma
                  }
                  options={pnlChart.options}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Theta'
                  series={
                    filteredAccount
                      ? greeksData?.result?.theta?.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : greeksData?.result?.theta
                  }
                  options={pnlChart.options}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Vega'
                  series={
                    filteredAccount
                      ? greeksData?.result?.vega?.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : greeksData?.result?.vega
                  }
                  options={pnlChart.options}
                />
              </Card>
            </Col>
          </Row>
        </Spin>

        <div className='Header-top'>
          <Title level={5} className='mb-0'>
            Greeks PnL
          </Title>
        </div>

        <Spin spinning={greeksPnlLoading} tip='Loading...'>
          <Row gutter={[24, 0]}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Delta PnL'
                  series={
                    filteredAccount
                      ? greeksPnlData?.result?.net_delta_pnl?.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : greeksPnlData?.result?.net_delta_pnl
                  }
                  options={pnlChart.options}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Gamma PnL'
                  series={
                    filteredAccount
                      ? greeksPnlData?.result?.gamma_pnl?.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : greeksPnlData?.result?.gamma_pnl
                  }
                  options={pnlChart.options}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Theta PnL'
                  series={
                    filteredAccount
                      ? greeksPnlData?.result?.theta_pnl?.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : greeksPnlData?.result?.theta_pnl
                  }
                  options={pnlChart.options}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Vega PnL'
                  series={
                    filteredAccount
                      ? greeksPnlData?.result?.vega_pnl?.filter(
                          (item) => item.name === filteredAccount.account_name
                        )
                      : greeksPnlData?.result?.vega_pnl
                  }
                  options={pnlChart.options}
                />
              </Card>
            </Col>
          </Row>
        </Spin>
      </div>
    </>
  );
}

export default Home;
